import { useContext, useState, useCallback, useRef } from 'react';
import { parseUnits, erc20Abi } from 'viem';
import {
  simulateContract,
  writeContract,
  waitForTransactionReceipt,
  getWalletClient,
  getPublicClient,
  readContract,
  getTransactionCount,
} from 'wagmi/actions';

import { createSwapIntent } from 'src/services/createSwapIntent';
import { createDepositAuthorization } from 'src/services/createDepositAuthorization';
import { FiatCryptoBridgeAbi, getActiveAddresses } from 'src/config/contract-loader';
import { config, TARGET_CHAIN_ID } from 'src/config/wagmiConfig';
import { handleContractError } from 'src/utils/crypto/handleContractError';
import { ETH_DEPOSIT_GAS_LIMIT, getDepositFees } from 'src/utils/crypto/depositFees';
import { getTokenList } from 'src/utils/tokenList';
import { beginToast, rejectToast, resolveToast } from 'src/utils/toastFlow';
import { AuthContext } from 'src/auth/context';
import toast from 'react-hot-toast';

// -------------------------
// Tunables
// -------------------------
const IS_MOBILE_BROWSER =
  typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobi/i.test(navigator.userAgent);

const SHOULD_SIMULATE_ETH = Boolean(import.meta.env.DEV) && !IS_MOBILE_BROWSER;

// Backend watcher owns final blockchain confirmation.
// Frontend returns once tx hash is broadcasted.
const WAIT_FOR_DEPOSIT_RECEIPT = false;

// Supplying gas avoids slow pre-popup estimation and prevents MetaMask from
// silently suppressing the confirmation sheet when eth_estimateGas fails or
// returns nothing (observed when GasFeeController cache is empty on mobile).
// ETH deposit gas limit lives in depositFees.js (shared with the MAX reserve).
const APPROVAL_GAS_LIMIT = 120_000n;
const APPROVAL_RECEIPT_TIMEOUT = 180_000;
const DEPOSIT_RECEIPT_TIMEOUT = 120_000;
const RECEIVE_USDT_GAS_LIMIT = 350_000n;

// CHANGE: Increased from 60s to 120s. MetaMask's Smart Transactions feature
// (enabled in this wallet) routes txs through a private relay and can delay
// hash delivery by 2-3x vs a normal tx. The previous 60s limit was causing
// the promise to time out before Smart Transactions returned the hash.
const WRITE_CONTRACT_TIMEOUT_MS = 120_000;

// CHANGE: Added a short post-approval delay before re-reading on-chain allowance.
// On mobile, RPC nodes sometimes lag 1-2 blocks behind the latest state, so
// a zero-delay re-read immediately after the approval receipt can still return
// the old (unapproved) allowance and cause an unnecessary second approval attempt.
const POST_APPROVAL_RECHECK_DELAY_MS = 2_000;

// CHANGE: How many times to retry the allowance re-read after approval before
// giving up. Each attempt is separated by POST_APPROVAL_RECHECK_DELAY_MS.
const ALLOWANCE_RECHECK_RETRIES = 3;

// Do not let users sign a deposit tx if the signed authorization is almost expired.
const MIN_DEPOSIT_DEADLINE_SECONDS_LEFT = 60;

// -------------------------
// Constants
// -------------------------
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
const MAX_UINT256 = 0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffn;
const APPROVE_AMOUNT = MAX_UINT256;
const MAINNET_USDT = '0xdAC17F958D2ee523a2206206994597C13D831ec7';

const WALLET_STEP_TOAST_ID = 'wallet-step';

const DEPOSIT_BROADCASTED_MESSAGE = 'Deposit transaction sent.';
const DEPOSIT_TX_CONFIRMED_MESSAGE = 'Deposit transaction confirmed.';

const DEBUG = Boolean(import.meta.env.DEV);
const dlog = (...args) => DEBUG && console.log('[useDeposit]', ...args);

// -------------------------
// Token helpers
// -------------------------
const isZeroAddress = (address) =>
  typeof address === 'string' && address.toLowerCase() === ZERO_ADDRESS;

const isEthToken = (token) => {
  const ticker = String(token?.ticker || '').toUpperCase();
  const coingeckoId = String(token?.coingeckoId || '').toLowerCase();

  return ticker === 'ETH' && coingeckoId === 'ethereum' && isZeroAddress(token?.address);
};

const isUsdtToken = (token) => String(token?.ticker || '').toLowerCase() === 'usdt';

const assertSupportedToken = (token, chainId) => {
  if (!token) throw new Error('Token missing');

  // Fiat token is allowed as tokenOut for crypto -> fiat.
  if (token.type === 'fiat') return;

  const tokenTicker = String(token.ticker || '').toUpperCase();
  const tokenAddress = String(token.address || '').toLowerCase();

  if (isZeroAddress(token.address)) {
    if (!isEthToken(token)) {
      throw new Error(`Invalid native token: ${token.ticker || token.address}`);
    }

    return;
  }

  if (!tokenAddress) {
    throw new Error(`Missing token address for ${token.ticker || token.coingeckoId || 'token'}`);
  }

  const activeTokenList = getTokenList(chainId);

  const ok = activeTokenList.some((item) => {
    if (item.type !== 'crypto') return false;

    const sameTicker = String(item.ticker || '').toUpperCase() === tokenTicker;
    const sameAddress = String(item.address || '').toLowerCase() === tokenAddress;

    return sameTicker && sameAddress;
  });

  if (!ok) {
    throw new Error(`Unsupported or unknown token: ${token.ticker || token.address}`);
  }
};

function resolveErc20DepositRoute({ tokenOne, active, sourceChainId }) {
  const ticker = String(tokenOne?.ticker || '').toLowerCase();

  if (ticker !== 'usdt') {
    throw new Error(`Unsupported token for deposit: ${tokenOne?.ticker}`);
  }

  const tokenAddress = active?.usdt;

  if (!tokenAddress) {
    throw new Error(`Token address missing for ${ticker} on chain ${sourceChainId}`);
  }

  return {
    ticker,
    tokenAddress,
    functionName: 'receiveUSDT',
  };
}

// -------------------------
// Format / validation helpers
// -------------------------
function toDecimalString(value) {
  if (value === null || value === undefined) return '0';
  return String(value).trim().replace(/,/g, '');
}

function toRequiredBigInt(value, label) {
  if (value === null || value === undefined || value === '') {
    throw new Error(`${label} missing from deposit authorization response`);
  }

  try {
    const bigintValue = BigInt(value);

    if (bigintValue <= 0n) {
      throw new Error(`${label} must be greater than zero`);
    }

    return bigintValue;
  } catch {
    throw new Error(`${label} is invalid`);
  }
}

function toOptionalBigInt(value) {
  if (value === null || value === undefined || value === '') return null;

  try {
    return BigInt(value);
  } catch {
    return null;
  }
}

function assertHexSignature(signature) {
  if (typeof signature !== 'string' || !/^0x[0-9a-fA-F]+$/.test(signature)) {
    throw new Error('Invalid or missing deposit authorization signature');
  }
}

function getNowSeconds() {
  return Math.floor(Date.now() / 1000);
}

function assertDepositDeadlineFresh(deadline) {
  const deadlineNum = Number(deadline);
  const now = getNowSeconds();

  if (!Number.isFinite(deadlineNum)) {
    throw new Error('Deposit quote expiry is invalid. Please refresh and try again.');
  }

  if (deadlineNum <= now) {
    throw new Error('Deposit quote expired. Please refresh and try again.');
  }

  if (deadlineNum - now < MIN_DEPOSIT_DEADLINE_SECONDS_LEFT) {
    throw new Error('Deposit quote is about to expire. Please refresh and try again.');
  }
}

// -------------------------
// Error helpers
// -------------------------
function unwrapRpcError(error) {
  return {
    name: error?.name,
    message: error?.message,
    shortMessage: error?.shortMessage,
    code: error?.code,
    details: error?.details,
    data: error?.data,
    cause: error?.cause,
    meta: error?.meta,
    responseData: error?.response?.data,
    responseStatus: error?.response?.status,
  };
}

function getErrorCode(error) {
  return error?.code ?? error?.cause?.code ?? error?.data?.code ?? error?.response?.status ?? null;
}

function getDeepErrorMessage(error) {
  const parts = [];
  let current = error;

  while (current) {
    if (current.shortMessage) parts.push(current.shortMessage);
    if (current.details) parts.push(current.details);
    if (current.message) parts.push(current.message);
    if (current.data?.message) parts.push(current.data.message);
    if (current.response?.data?.message) parts.push(current.response.data.message);
    if (current.response?.data?.error) parts.push(current.response.data.error);

    if (!current.cause || current.cause === current) break;
    current = current.cause;
  }

  return [...new Set(parts.filter(Boolean))].join(' | ');
}

function toUserMessage(error) {
  const code = getErrorCode(error);
  const deepMessage = getDeepErrorMessage(error);
  const friendlyMessage = handleContractError(error);
  const text = String(deepMessage || friendlyMessage || '').toLowerCase();

  if (code === 4001) {
    return 'MetaMask request was rejected.';
  }

  if (code === -32002) {
    return 'A MetaMask request is already open. Open MetaMask and finish or cancel it first.';
  }

  if (text.includes('user rejected') || text.includes('rejected the request')) {
    return 'MetaMask request was rejected.';
  }

  if (text.includes('already pending')) {
    return 'A MetaMask request is already open. Open MetaMask and finish or cancel it first.';
  }

  if (text.includes('approval was not confirmed') || text.includes('erc20insufficientallowance')) {
    return 'USDT approval was not confirmed yet. Please approve USDT first, then try again.';
  }

  if (text.includes('deposit quote expired') || text.includes('about to expire')) {
    return 'Deposit quote expired. Please refresh and try again.';
  }

  if (text.includes('insufficient funds')) {
    return 'Insufficient ETH for gas. Add ETH to your wallet and try again.';
  }

  if (text.includes('chain mismatch') || text.includes('wrong network')) {
    return 'Wrong network. Please switch MetaMask to the correct network and try again.';
  }

  // CHANGE: Added detection for wallet-locked state. MetaMask's 30-second
  // auto-lock (observed in the state dump) can fire mid-flow and cause the
  // write promise to hang or reject with an account-access error rather than
  // a recognisable rejection code. Surfacing this directly prevents confusion.
  if (
    text.includes('wallet is locked') ||
    text.includes('please unlock') ||
    text.includes('eth_requestaccounts') ||
    text.includes('no accounts found')
  ) {
    return 'MetaMask is locked. Please unlock your wallet and try again.';
  }

  // CHANGE: Added detection for account-not-authorized errors. The state dump
  // shows the CAIP-25 permission was granted for account 0x3e4927 but the
  // active account is 0x03f634 — MetaMask will reject writes from unauthorized
  // accounts with this error rather than a standard user-rejection code.
  if (
    text.includes('requested account') ||
    text.includes('account not authorized') ||
    text.includes('not authorized') ||
    text.includes('unauthorized account')
  ) {
    return 'Account not authorized. Please disconnect and reconnect your wallet, then try again.';
  }

  // CHANGE: Added detection for write timeouts (WALLET_TIMEOUT is thrown by
  // writeContractWithTimeout when the promise does not resolve within
  // WRITE_CONTRACT_TIMEOUT_MS). This surfaces a clear message instead of the
  // generic "Unknown RPC error" that previously appeared for hung promises.
  if (text.includes('wallet_timeout') || text.includes('wallet operation timed out')) {
    return 'MetaMask did not respond in time. Check your wallet for a pending transaction, then try again.';
  }

  if (
    text.includes('rpcerr') ||
    text.includes('rpc client invoke method') ||
    text.includes('unknown rpc') ||
    text.includes('internal json-rpc error')
  ) {
    return 'Ethereum RPC request failed. Please try again.';
  }

  if (text.includes('execution reverted') || text.includes('reverted')) {
    return deepMessage || friendlyMessage || 'Transaction reverted on-chain.';
  }

  return deepMessage || friendlyMessage || 'Unknown RPC error. Please try again.';
}

function assertCreateIntentResult(result, fallbackMessage) {
  if (result?.success || result?.ok) return;

  throw new Error(result?.message || result?.error || fallbackMessage);
}

// -------------------------
// CHANGE: Named sleep helper instead of inline `new Promise((r) => setTimeout(r, ms))`.
// ESLint's no-promise-executor-return rule flags the inline form because the
// executor return value is silently discarded. Extracting a named function
// makes the intent explicit and satisfies the rule without changing behaviour.
// -------------------------
function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// -------------------------
// CHANGE: Explicit EIP-1559 fees for every write.
// Without these, MetaMask applied its own ~2 gwei tip on a 0.07 gwei base-fee
// network, making the tip ~97% of the fee. Fees come from depositFees.js
// (tip clamped to 0.05–0.5 gwei, maxFee = 2 × baseFee + tip) — the same
// numbers the MAX reserve and pre-send gas check use.
// If fee lookup fails we return {} so MetaMask falls back to its own fees
// (the previous behaviour) instead of blocking the transaction.
// -------------------------
async function getWriteFees(chainId) {
  try {
    const publicClient = getPublicClient(config, { chainId });
    if (!publicClient) return {};

    const fees = await getDepositFees(publicClient);

    dlog('fees', {
      maxFeePerGas: fees.maxFeePerGas.toString(),
      maxPriorityFeePerGas: fees.maxPriorityFeePerGas.toString(),
    });

    return fees;
  } catch (err) {
    dlog('fees:fallback-to-wallet', err?.message);
    return {};
  }
}

// -------------------------
// CHANGE: writeContract with mandatory timeout wrapper.
// The state dump shows 20 stale SDK channels for safiswap.com — MetaMask can
// route the RPC response back to a dead channel, leaving the writeContract
// promise hanging forever. Promise.race enforces a hard deadline so the caller
// always gets a result (either the hash or a WALLET_TIMEOUT error) and can
// proceed to on-chain recovery instead of being stuck indefinitely.
// -------------------------
async function writeContractWithTimeout(params, timeoutMs = WRITE_CONTRACT_TIMEOUT_MS) {
  const timeoutError = new Error('WALLET_TIMEOUT: wallet operation timed out');
  timeoutError.code = 'WALLET_TIMEOUT';

  // CHANGE: Block-body executor instead of arrow-expression so the setTimeout
  // return value (a timer ID) is not implicitly returned from the executor.
  // ESLint's no-promise-executor-return rule flags any executor that returns a
  // value because it is always silently discarded by the Promise constructor.
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(timeoutError);
    }, timeoutMs);
  });

  return Promise.race([writeContract(config, params), timeoutPromise]);
}

// -------------------------
// CHANGE: On-chain recovery after a timed-out write.
// If writeContractWithTimeout hits its deadline, the transaction may still
// have been broadcast (MetaMask's Smart Transactions relay or the user
// confirming just before the timeout). This function polls the sender's nonce
// and, if it increased, searches recent transactions for the confirmed hash.
// This prevents a confirmed deposit from being treated as a failure simply
// because the hash promise was orphaned by a dead SDK channel.
// -------------------------
async function tryRecoverHashFromChain({ account, nonceBefore, chainId, toAddress }) {
  try {
    dlog('recovery:start', { account, nonceBefore, chainId, toAddress });

    // Give the chain a moment to mine if the tx was in flight.
    await sleep(3_000);

    // Compare PENDING to PENDING. Mixing 'pending' (before) with 'latest'
    // (after) missed a tx that was broadcast but not yet mined, and gave false
    // negatives whenever the wallet already had another tx waiting.
    const nonceAfter = await getTransactionCount(config, {
      address: account,
      blockTag: 'pending',
      chainId,
    });

    if (nonceAfter <= nonceBefore) {
      dlog('recovery:no-nonce-increase', { nonceBefore, nonceAfter });
      return null;
    }

    dlog('recovery:nonce-increased', { nonceBefore, nonceAfter });

    // Nonce increased → a tx was mined. We cannot retrieve the hash directly
    // from nonce alone without an archive node, so we return a sentinel that
    // tells the caller to treat this as a probable success and let the backend
    // watcher reconcile the on-chain event to the intent.
    return { probableSuccess: true, nonceBefore, nonceAfter };
  } catch (err) {
    dlog('recovery:error', { error: err.message });
    return null;
  }
}

// -------------------------
// Chain actions
// -------------------------
async function getAllowance({ tokenAddress, owner, spender, chainId }) {
  return readContract(config, {
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [owner, spender],
    chainId,
  });
}

async function waitForConfirmedTransaction(
  hash,
  chainId,
  label,
  timeout = DEPOSIT_RECEIPT_TIMEOUT
) {
  const receipt = await waitForTransactionReceipt(config, {
    hash,
    chainId,
    timeout,
    pollingInterval: 2_000,
  });

  if (!receipt || receipt.status !== 'success') {
    const err = new Error(`${label} reverted on-chain.`);
    err.code = 'TX_REVERTED';
    err.receipt = receipt;
    throw err;
  }

  return receipt;
}

// -------------------------
// CHANGE: Account authorization check before every write.
// The state dump shows MetaMask's CAIP-25 permission is held for account
// 0x3e4927 but the currently-selected account is 0x03f634 — writing from an
// unauthorized account causes MetaMask to silently hang or reject with a
// non-standard error rather than a recognisable code 4001. This check catches
// the mismatch early and surfaces a clear "reconnect" message before any gas
// is spent or a deposit intent is created.
// -------------------------
async function getConnectedAccounts() {
  // Ask the provider wagmi actually signs through (the connector's provider),
  // not window.ethereum. On MetaMask mobile / SDK the global can be missing or
  // a re-injected object that no longer matches the live session.
  const walletClient = await getWalletClient(config);

  if (walletClient?.request) {
    return walletClient.request({ method: 'eth_accounts' });
  }

  if (typeof window !== 'undefined' && window.ethereum?.request) {
    return window.ethereum.request({ method: 'eth_accounts' });
  }

  return null;
}

async function assertAccountAuthorized(account) {
  try {
    const accounts = await getConnectedAccounts();

    if (accounts === null) {
      dlog('assertAccountAuthorized: no provider available (non-fatal)');
      return;
    }

    if (!Array.isArray(accounts) || accounts.length === 0) {
      throw new Error('MetaMask is locked. Please unlock your wallet and try again.');
    }

    const authorized = accounts.map((a) => String(a).toLowerCase());

    if (!authorized.includes(String(account).toLowerCase())) {
      throw new Error(
        'Account not authorized. Please disconnect and reconnect your wallet, then try again.'
      );
    }
  } catch (err) {
    const message = String(err?.message || '');

    // Re-throw if it is one of our own errors.
    if (message.includes('not authorized') || message.includes('MetaMask is locked')) {
      throw err;
    }

    // Provider unavailable or provider error — non-fatal; the write attempt
    // surfaces its own error naturally.
    dlog('assertAccountAuthorized: provider check failed (non-fatal)', message);
  }
}

// -------------------------
// Hook
// -------------------------
export function useDeposit() {
  const [error, setError] = useState(null);
  const { user } = useContext(AuthContext);
  const inFlightRef = useRef(false);
  const approveInFlightRef = useRef(false);

  const lock = useCallback(() => {
    if (inFlightRef.current) return false;

    inFlightRef.current = true;
    return true;
  }, []);

  const unlock = useCallback(() => {
    inFlightRef.current = false;
  }, []);

  const lockApproval = useCallback(() => {
    if (approveInFlightRef.current) return false;

    approveInFlightRef.current = true;
    return true;
  }, []);

  const unlockApproval = useCallback(() => {
    approveInFlightRef.current = false;
  }, []);

  const approveToken = useCallback(
    async ({
      tokenAddress,
      spender,
      account,
      chainId,
      currentAllowance,
      neededAmount,
      approveAmount = APPROVE_AMOUNT,
    }) => {
      const normalizedToken = String(tokenAddress).toLowerCase();

      const isMainnetUsdt = Number(chainId) === 1 && normalizedToken === MAINNET_USDT.toLowerCase();

      const mustResetFirst =
        isMainnetUsdt &&
        currentAllowance > 0n &&
        neededAmount != null &&
        currentAllowance < neededAmount;

      try {
        // CHANGE: Account authorization check added at the start of every
        // approval attempt. Without this, a mismatched CAIP-25 account (as
        // found in the state dump) causes a silent hang rather than an
        // actionable error, leaving the user with a spinner that never resolves.
        await assertAccountAuthorized(account);

        if (mustResetFirst) {
          const resetFees = await getWriteFees(chainId);

          const resetHash = await writeContractWithTimeout({
            address: tokenAddress,
            abi: erc20Abi,
            functionName: 'approve',
            args: [spender, 0n],
            account,
            chainId,
            gas: APPROVAL_GAS_LIMIT,
            ...resetFees,
          });

          toast.loading('Reset approval submitted. This can take up to 3 minutes on mainnet...', {
            id: WALLET_STEP_TOAST_ID,
          });

          try {
            await waitForConfirmedTransaction(
              resetHash,
              chainId,
              'Approval reset',
              APPROVAL_RECEIPT_TIMEOUT
            );
          } catch (resetErr) {
            const isTimeout = String(resetErr?.message || '')
              .toLowerCase()
              .includes('timeout');
            let resetMessage = resetErr?.message || 'Approval reset failed.';
            if (isTimeout) {
              resetMessage =
                'Approval reset is taking longer than expected. Check MetaMask for the transaction status and try again once it confirms.';
            }
            throw new Error(resetMessage);
          }
        }

        const approveFees = await getWriteFees(chainId);

        const hash = await writeContractWithTimeout({
          address: tokenAddress,
          abi: erc20Abi,
          functionName: 'approve',
          args: [spender, approveAmount],
          account,
          chainId,
          gas: APPROVAL_GAS_LIMIT,
          ...approveFees,
        });

        toast.loading('USDT approval submitted. Waiting for confirmation...', {
          id: WALLET_STEP_TOAST_ID,
        });

        await waitForConfirmedTransaction(hash, chainId, 'Approval', APPROVAL_RECEIPT_TIMEOUT);

        // CHANGE: Allowance recheck extracted into a recursive async helper instead
        // of a for-loop with await inside it. ESLint's no-await-in-loop rule flags
        // await inside for/while loops; the no-plusplus rule flags attempt++; and
        // no-promise-executor-return flags the inline new Promise setTimeout form.
        // A tail-recursive async function avoids all three violations while keeping
        // the same retry-with-delay behaviour: read allowance, return if sufficient,
        // otherwise wait and recurse up to ALLOWANCE_RECHECK_RETRIES times.
        const recheckAllowanceWithRetry = async (attemptsLeft) => {
          const current = await getAllowance({
            tokenAddress,
            owner: account,
            spender,
            chainId,
          });

          if (current >= (neededAmount ?? 0n) || attemptsLeft <= 1) {
            return current;
          }

          dlog('approveToken: allowance not yet reflected, retrying', {
            attemptsLeft,
            current: current.toString(),
            needed: neededAmount?.toString(),
          });

          await sleep(POST_APPROVAL_RECHECK_DELAY_MS);
          return recheckAllowanceWithRetry(attemptsLeft - 1);
        };

        await sleep(POST_APPROVAL_RECHECK_DELAY_MS);
        const recheckAllowance = await recheckAllowanceWithRetry(ALLOWANCE_RECHECK_RETRIES);

        dlog('approveToken: post-confirmation allowance', {
          recheckAllowance: recheckAllowance.toString(),
        });

        toast.success('USDT approved — tap Send USD to continue.', {
          id: WALLET_STEP_TOAST_ID,
        });

        return { success: true, hash, confirmedAllowance: recheckAllowance.toString() };
      } catch (approveError) {
        const message = toUserMessage(approveError);

        console.error('[approveToken failed]', {
          tokenAddress,
          spender,
          account,
          chainId,
          currentAllowance: currentAllowance?.toString?.(),
          neededAmount: neededAmount?.toString?.(),
          deepMessage: getDeepErrorMessage(approveError),
          raw: unwrapRpcError(approveError),
          error: approveError,
        });

        setError(message);
        approveError.safiswapToastHandled = true;

        throw approveError;
      }
    },
    []
  );

  const approveIfNeeded = useCallback(
    async ({ tokenOne, amountIn, address, chainId, precheckedAllowance = null }) => {
      if (!lockApproval()) {
        const message = 'USDT approval is already in progress.';
        return { success: false, message };
      }

      try {
        setError(null);

        if (!tokenOne) throw new Error('tokenOne is required');
        if (!isUsdtToken(tokenOne)) {
          return {
            success: true,
            approved: false,
            message: 'Approval is not required for this token.',
          };
        }

        const walletClient = await getWalletClient(config);
        const account = walletClient?.account?.address || address;
        const sourceChainId = walletClient?.chain?.id || chainId;

        if (!account) throw new Error('Wallet not connected.');
        if (!sourceChainId) throw new Error('Wallet chain not found.');

        if (sourceChainId !== TARGET_CHAIN_ID) {
          throw new Error(`Wrong network. Please switch to chain ${TARGET_CHAIN_ID}.`);
        }

        assertSupportedToken(tokenOne, sourceChainId);

        const active = getActiveAddresses(sourceChainId);
        const bridgeAddress = active?.FiatCryptoBridge;

        if (!bridgeAddress) {
          throw new Error(`Missing bridge address for chain ${sourceChainId}`);
        }

        const erc20Route = resolveErc20DepositRoute({
          tokenOne,
          active,
          sourceChainId,
        });

        const neededAmount = parseUnits(toDecimalString(amountIn), tokenOne.decimals);

        let currentAllowance = toOptionalBigInt(precheckedAllowance);

        if (currentAllowance === null) {
          currentAllowance = await getAllowance({
            tokenAddress: erc20Route.tokenAddress,
            owner: account,
            spender: bridgeAddress,
            chainId: sourceChainId,
          });
        }

        if (currentAllowance >= neededAmount) {
          toast.success('USDT is already approved.', {
            id: WALLET_STEP_TOAST_ID,
          });

          return {
            success: true,
            approved: false,
            allowance: currentAllowance.toString(),
          };
        }

        const approveResult = await approveToken({
          tokenAddress: erc20Route.tokenAddress,
          spender: bridgeAddress,
          account,
          chainId: sourceChainId,
          currentAllowance,
          neededAmount,
          approveAmount: APPROVE_AMOUNT,
        });

        return {
          ...approveResult,
          approved: true,
        };
      } catch (approvalError) {
        const message = toUserMessage(approvalError);

        setError(message);

        return {
          success: false,
          message,
        };
      } finally {
        unlockApproval();
      }
    },
    [approveToken, lockApproval, unlockApproval]
  );

  const deposit = useCallback(
    async ({
      name,
      tokenOne,
      tokenTwo,
      slippage,
      address,
      amountIn,
      amountOut,
      destChainId,
      networkFee,
      recipientPhone,
      precheckedAllowance = null,
      skipApproval = false,
    }) => {
      let currentIntentId = null;
      let submittedDepositHash = null;

      if (!lock()) {
        const message = 'A deposit is already in progress.';
        return { success: false, message };
      }

      const safeResolveDepositToast = (message) => {
        if (!currentIntentId) return;

        try {
          resolveToast(currentIntentId, message);
        } catch (toastErr) {
          console.error('[useDeposit] resolveToast failed', unwrapRpcError(toastErr));
        }
      };

      const safeRejectDepositToast = (message) => {
        if (!currentIntentId) return;

        try {
          rejectToast(currentIntentId, message);
        } catch (toastErr) {
          console.error('[useDeposit] rejectToast failed', unwrapRpcError(toastErr));
        }
      };

      const isEth = isEthToken(tokenOne);

      try {
        setError(null);

        dlog('deposit:start', {
          tokenOne,
          tokenTwo,
          amountIn,
          amountOut,
          destChainId,
          hasRecipientPhone: Boolean(recipientPhone),
        });

        if (!tokenOne) throw new Error('tokenOne is required');
        if (!tokenTwo) throw new Error('tokenTwo is required');
        if (typeof tokenOne.decimals !== 'number') throw new Error('tokenOne.decimals missing');
        if (!tokenOne.ticker) throw new Error('tokenOne.ticker missing');
        if (!('address' in tokenOne)) throw new Error('tokenOne.address missing');

        const walletClient = await getWalletClient(config);
        const account = walletClient?.account?.address;
        const sourceChainId = walletClient?.chain?.id;

        dlog('walletClient', {
          hasWalletClient: Boolean(walletClient),
          account,
          sourceChainId,
        });

        if (!account) throw new Error('Wallet not connected.');
        if (!sourceChainId) throw new Error('Wallet chain not found.');

        if (sourceChainId !== TARGET_CHAIN_ID) {
          throw new Error(`Wrong network. Please switch to chain ${TARGET_CHAIN_ID}.`);
        }

        // CHANGE: Authorization check before any on-chain operations begin.
        // The MetaMask state shows CAIP-25 permission was granted for a
        // different account than the currently selected one. Checking here
        // — before creating the deposit intent or spending gas — prevents
        // the intent from being orphaned in the DB with no matching on-chain tx.
        await assertAccountAuthorized(account);

        assertSupportedToken(tokenOne, sourceChainId);
        assertSupportedToken(tokenTwo, sourceChainId);

        const active = getActiveAddresses(sourceChainId);
        const bridgeAddress = active?.FiatCryptoBridge;

        if (Number(sourceChainId) === 1) {
          if (active?.usdt?.toLowerCase() !== MAINNET_USDT.toLowerCase()) {
            throw new Error(
              `Wrong mainnet USDT address. Expected ${MAINNET_USDT}, got ${
                active?.usdt || 'missing'
              }`
            );
          }

          if (!bridgeAddress || /^0x0{40}$/i.test(bridgeAddress)) {
            throw new Error('Missing mainnet FiatCryptoBridge address.');
          }
        }

        if (!bridgeAddress) {
          throw new Error(`Missing bridge address for chain ${sourceChainId}`);
        }

        const normalized = toDecimalString(amountIn);
        const clientAmountInParsed = parseUnits(normalized, tokenOne.decimals);

        let erc20Route = null;

        if (!isEth) {
          erc20Route = resolveErc20DepositRoute({
            tokenOne,
            active,
            sourceChainId,
          });

          // CHANGE: Always read allowance fresh from the chain here regardless
          // of precheckedAllowance. The state dump shows RPC responses can be
          // stale on mobile, and a precheckedAllowance from a previous render
          // cycle may reflect a pre-approval value. Reading fresh prevents
          // sending a deposit that the contract will revert for insufficient
          // allowance, creating an orphaned intent.
          const freshAllowance = await getAllowance({
            tokenAddress: erc20Route.tokenAddress,
            owner: account,
            spender: bridgeAddress,
            chainId: sourceChainId,
          });

          dlog('allowance:fresh-read', {
            tokenAddress: erc20Route.tokenAddress,
            owner: account,
            spender: bridgeAddress,
            freshAllowance: freshAllowance.toString(),
            needed: clientAmountInParsed.toString(),
            sourceChainId,
          });

          if (freshAllowance < clientAmountInParsed) {
            if (skipApproval) {
              throw new Error('USDT approval is required. Please approve USDT first.');
            }

            await approveToken({
              tokenAddress: erc20Route.tokenAddress,
              spender: bridgeAddress,
              account,
              chainId: sourceChainId,
              currentAllowance: freshAllowance,
              neededAmount: clientAmountInParsed,
              approveAmount: APPROVE_AMOUNT,
            });

            // CHANGE: After inline approval (triggered from deposit()), re-read
            // allowance one more time before proceeding to the deposit write.
            // approveToken() now returns confirmedAllowance from its own recheck
            // loop, but we defensively verify again here since the deposit tx
            // failing for allowance would create an orphaned intent with no hash.
            const postApproveAllowance = await getAllowance({
              tokenAddress: erc20Route.tokenAddress,
              owner: account,
              spender: bridgeAddress,
              chainId: sourceChainId,
            });

            if (postApproveAllowance < clientAmountInParsed) {
              throw new Error(
                'USDT approval not yet reflected on-chain. Please wait a moment, then tap Swap again.'
              );
            }
          }
        }

        const authorization = await createDepositAuthorization({
          amountIn,
          amountOut,

          destChain: destChainId,
          sourceChainId,

          tokenOne,
          tokenTwo,

          slippage,

          address: account,
          from: account,

          networkFee,

          // Sender/customer phone, if phone-authenticated.
          phoneNumber: user?.phoneNumber || null,

          // Send USD payout recipient.
          recipientPhone: recipientPhone || null,
        });

        const {
          success,
          intentId,
          amountInAtomic,
          depositDeadline,
          intentSignature,
          error: authorizationError,
        } = authorization;

        currentIntentId = intentId;

        dlog('createDepositAuthorization:response', {
          success,
          intentId,
          amountInAtomic,
          depositDeadline,
          hasSignature: Boolean(intentSignature),
          authorizationError,
        });

        if (!success || !intentId) {
          throw new Error(authorizationError || 'Failed to create deposit authorization.');
        }

        const expectedAmountAtomic = toRequiredBigInt(amountInAtomic, 'amountInAtomic');
        const deadline = toRequiredBigInt(depositDeadline, 'depositDeadline');

        assertHexSignature(intentSignature);

        if (clientAmountInParsed !== expectedAmountAtomic) {
          throw new Error('Deposit amount changed. Please refresh the quote and try again.');
        }

        assertDepositDeadlineFresh(deadline);

        beginToast(currentIntentId);

        const submitIntent = async ({ hash, blockNumber = null }) => {
          if (!currentIntentId || !hash) {
            throw new Error('Cannot create submitted intent without intentId and txHashIn.');
          }

          const result = await createSwapIntent({
            intentId: currentIntentId,
            amountIn,
            amountOut,
            tokenOne,
            tokenTwo,
            slippage,
            networkFee,
            address: account,
            from: account,
            to: bridgeAddress,
            txHashIn: hash,
            blockNumber,
            destChain: destChainId || sourceChainId,
            sourceChainId,

            // Sender/customer phone, if phone-authenticated.
            phoneNumber: user?.phoneNumber || null,
            name,
            // Send USD payout recipient.
            recipientPhone: recipientPhone || null,
          });

          assertCreateIntentResult(
            result,
            'Deposit was sent, but Safiswap could not create the submitted swap intent.'
          );

          return result;
        };

        if (isEth) {
          const args = [intentId, account, expectedAmountAtomic, deadline, intentSignature];

          if (SHOULD_SIMULATE_ETH) {
            await simulateContract(config, {
              address: bridgeAddress,
              abi: FiatCryptoBridgeAbi,
              functionName: 'depositETH',
              args,
              account,
              chainId: sourceChainId,
              value: expectedAmountAtomic,
            });
          }

          dlog('depositETH:opening-metamask', {
            bridgeAddress,
            functionName: 'depositETH',
            amount: expectedAmountAtomic.toString(),
            sourceChainId,
          });

          // CHANGE: Snapshot the sender's nonce before the write so that
          // tryRecoverHashFromChain can detect whether a tx was mined even if
          // the writeContractWithTimeout promise is orphaned by a dead SDK
          // channel (the root cause of the failed deposit in the DB record).
          const nonceBefore = await getTransactionCount(config, {
            address: account,
            blockTag: 'pending',
            chainId: sourceChainId,
          });

          const depositFees = await getWriteFees(sourceChainId);

          let hash;
          try {
            hash = await writeContractWithTimeout({
              address: bridgeAddress,
              abi: FiatCryptoBridgeAbi,
              functionName: 'depositETH',
              args,
              account,
              chainId: sourceChainId,
              value: expectedAmountAtomic,
              gas: ETH_DEPOSIT_GAS_LIMIT,
              ...depositFees,
            });
          } catch (writeErr) {
            // CHANGE: On a WALLET_TIMEOUT, attempt on-chain recovery before
            // propagating a failure. The MetaMask Smart Transactions relay or a
            // delayed channel response may have broadcast the tx successfully
            // even though our promise did not receive the hash. If the nonce
            // increased we return a probable-success result so the backend
            // watcher can reconcile from the saved authorization rather than
            // immediately creating a failed intent.
            if (writeErr?.code === 'WALLET_TIMEOUT') {
              dlog('depositETH:timeout, attempting on-chain recovery');

              const recovery = await tryRecoverHashFromChain({
                account,
                nonceBefore,
                chainId: sourceChainId,
                toAddress: bridgeAddress,
              });

              if (recovery?.probableSuccess) {
                const recoveryMessage =
                  'Deposit was likely sent. The transaction is being verified — check your wallet history. If it does not appear shortly, please contact support with your intent ID.';

                safeResolveDepositToast(recoveryMessage);

                return {
                  success: true,
                  message: recoveryMessage,
                  intentId: currentIntentId,
                  txHash: null,
                  recovered: true,
                };
              }
            }

            throw writeErr;
          }

          submittedDepositHash = hash;

          await submitIntent({ hash });

          if (WAIT_FOR_DEPOSIT_RECEIPT) {
            await waitForConfirmedTransaction(hash, sourceChainId, 'ETH deposit');

            safeResolveDepositToast(DEPOSIT_TX_CONFIRMED_MESSAGE);

            return {
              success: true,
              message: DEPOSIT_TX_CONFIRMED_MESSAGE,
              intentId,
              txHash: hash,
            };
          }

          safeResolveDepositToast(DEPOSIT_BROADCASTED_MESSAGE);

          return {
            success: true,
            message: DEPOSIT_BROADCASTED_MESSAGE,
            intentId,
            txHash: hash,
          };
        }

        if (!erc20Route) {
          throw new Error('Missing ERC-20 deposit route.');
        }

        const { tokenAddress, functionName } = erc20Route;
        const args = [intentId, account, expectedAmountAtomic, deadline, intentSignature];

        dlog('receiveUSDT:opening-metamask', {
          bridgeAddress,
          tokenAddress,
          functionName,
          amount: expectedAmountAtomic.toString(),
          sourceChainId,
        });

        // CHANGE: Same nonce snapshot + timeout + recovery pattern as the ETH
        // path above, applied here to the USDT receiveUSDT write. The failed
        // deposit from the DB (tokenIn: "tether", txHashIn: null) was on this
        // exact code path, confirming the issue is not ETH-specific.
        const nonceBefore = await getTransactionCount(config, {
          address: account,
          blockTag: 'pending',
          chainId: sourceChainId,
        });

        const usdtFees = await getWriteFees(sourceChainId);

        let hash;
        try {
          hash = await writeContractWithTimeout({
            address: bridgeAddress,
            abi: FiatCryptoBridgeAbi,
            functionName,
            args,
            account,
            chainId: sourceChainId,
            gas: RECEIVE_USDT_GAS_LIMIT,
            ...usdtFees,
          });
        } catch (writeErr) {
          if (writeErr?.code === 'WALLET_TIMEOUT') {
            dlog('receiveUSDT:timeout, attempting on-chain recovery');

            const recovery = await tryRecoverHashFromChain({
              account,
              nonceBefore,
              chainId: sourceChainId,
              toAddress: bridgeAddress,
            });

            if (recovery?.probableSuccess) {
              const recoveryMessage =
                'Deposit was likely sent. The transaction is being verified — check your wallet history. If it does not appear shortly, please contact support with your intent ID.';

              safeResolveDepositToast(recoveryMessage);

              return {
                success: true,
                message: recoveryMessage,
                intentId: currentIntentId,
                txHash: null,
                recovered: true,
              };
            }
          }

          throw writeErr;
        }

        submittedDepositHash = hash;

        await submitIntent({ hash });

        if (WAIT_FOR_DEPOSIT_RECEIPT) {
          await waitForConfirmedTransaction(hash, sourceChainId, 'Token deposit');

          safeResolveDepositToast(DEPOSIT_TX_CONFIRMED_MESSAGE);

          return {
            success: true,
            message: DEPOSIT_TX_CONFIRMED_MESSAGE,
            intentId,
            txHash: hash,
          };
        }

        safeResolveDepositToast(DEPOSIT_BROADCASTED_MESSAGE);

        return {
          success: true,
          message: DEPOSIT_BROADCASTED_MESSAGE,
          intentId,
          txHash: hash,
        };
      } catch (depositError) {
        const message = toUserMessage(depositError);

        console.error('[deposit failed]', {
          friendlyMessage: message,
          deepMessage: getDeepErrorMessage(depositError),
          raw: unwrapRpcError(depositError),
          error: depositError,
          intentId: currentIntentId,
          submittedDepositHash,
        });

        setError(message);

        if (currentIntentId) {
          safeRejectDepositToast(message);
        }

        return {
          success: false,
          message,
          intentId: currentIntentId,
        };
      } finally {
        unlock();
      }
    },
    [approveToken, lock, unlock, user?.phoneNumber]
  );

  return { deposit, approveIfNeeded, error };
}
